import { useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'
import { resolveBookIdentity } from '../lib/bookLogoRegistry.js'
import { fetchLiveOddsHistory } from '../lib/liveOddsVnextEvidence.js'
import { buildHistoryKey, normalizeHistorySamples } from './LiveOddsVNextHistoryChart.jsx'

const COLORS = ['#35d6ed', '#f7bd39', '#4de3a1', '#ff7389', '#9a83ff', '#ff9f43', '#62a8ff', '#d77cff']
let historyPromise = null

function impliedProbability(american) {
  const number = Number(american)
  if (number >= 100) return 100 / (number + 100)
  if (number <= -100) return Math.abs(number) / (Math.abs(number) + 100)
  return null
}

function oddsLabel(value) {
  const number = Math.round(Number(value))
  return number > 0 ? `+${number}` : String(number)
}

function timeLabel(value) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value))
}

function loadHistory() {
  historyPromise ||= fetchLiveOddsHistory().catch((error) => {
    historyPromise = null
    throw error
  })
  return historyPromise
}

function buildSeries(history, cells) {
  return Object.values(cells || {}).map((cell) => {
    const row = cell?.row
    const key = buildHistoryKey(row)
    const samples = normalizeHistorySamples(history, key).map((sample) => ({
      ...sample,
      probability: impliedProbability(sample.odds),
    })).filter((sample) => sample.probability != null)
    return { book: row?.book, key, samples }
  }).filter((series) => series.book && series.samples.length > 0)
}

function chartGeometry(series) {
  const samples = series.flatMap((item) => item.samples)
  if (samples.length === 0) return null
  const minTime = Math.min(...samples.map((sample) => sample.time))
  const maxTime = Math.max(...samples.map((sample) => sample.time))
  const minProbability = Math.min(...samples.map((sample) => sample.probability))
  const maxProbability = Math.max(...samples.map((sample) => sample.probability))
  return {
    minTime,
    maxTime,
    timeSpan: Math.max(1, maxTime - minTime),
    minProbability,
    maxProbability,
    probabilitySpan: Math.max(0.01, maxProbability - minProbability),
  }
}

function point(sample, range) {
  const x = range.minTime === range.maxTime ? 390 : 42 + ((sample.time - range.minTime) / range.timeSpan) * 696
  const y = 20 + ((range.maxProbability - sample.probability) / range.probabilitySpan) * 154
  return { ...sample, x, y }
}

export default function LiveOddsVNextMultiBookHistoryChart({ market, selection, onClose }) {
  const [history, setHistory] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    let active = true
    loadHistory().then((value) => active && setHistory(value)).catch((reason) => active && setError(String(reason?.message || reason)))
    return () => { active = false }
  }, [])
  const series = useMemo(() => history ? buildSeries(history, market?.cells?.[selection]) : [], [history, market, selection])
  const range = chartGeometry(series)
  const sampleCount = series.reduce((sum, item) => sum + item.samples.length, 0)
  const movementSeries = series.filter((item) => item.samples.length > 1).length

  return <section className="lovxt-history" aria-label={`${selection} multi-book odds history`}>
    <header>
      <div><small>Stored sportsbook tape</small><h4>{selection} · {market?.betType}</h4></div>
      <div className="lovxt-history-facts"><span>{series.length} books</span><span>{sampleCount} samples</span><span>{movementSeries} with movement</span></div>
      <button type="button" onClick={onClose} aria-label="Close odds history"><X size={15} /></button>
    </header>
    {!history && !error && <div className="lovxt-history-state">Loading the actual stored history artifact…</div>}
    {error && <div className="lovxt-history-state warn">History unavailable: {error}</div>}
    {history && !range && <div className="lovxt-history-state">No stored samples exist for this exact selection. Current prices remain visible above; no history is invented.</div>}
    {range && <>
      <svg viewBox="0 0 780 220" role="img" aria-label={`${sampleCount} real price-history samples across ${series.length} sportsbooks`}>
        {[20, 97, 174].map((y) => <line key={y} x1="42" x2="738" y1={y} y2={y} className="lovxt-grid" />)}
        {series.map((item, index) => {
          const points = item.samples.map((sample) => point(sample, range))
          const path = points.map((value, pointIndex) => `${pointIndex === 0 ? 'M' : 'L'} ${value.x.toFixed(2)} ${value.y.toFixed(2)}`).join(' ')
          const color = COLORS[index % COLORS.length]
          return <g key={item.book}>
            {points.length > 1 && <path d={path} fill="none" stroke={color} strokeWidth="2" />}
            {points.map((value, pointIndex) => <circle key={`${value.time}|${pointIndex}`} cx={value.x} cy={value.y} r="3" fill="#09111a" stroke={color} strokeWidth="2"><title>{resolveBookIdentity(item.book).displayName} · {timeLabel(value.time)} · {oddsLabel(value.odds)}</title></circle>)}
          </g>
        })}
        <text x="42" y="208">{timeLabel(range.minTime)}</text><text x="738" y="208" textAnchor="end">{timeLabel(range.maxTime)}</text>
      </svg>
      <div className="lovxt-legend">{series.map((item, index) => <span key={item.book}><i style={{ background: COLORS[index % COLORS.length] }} />{resolveBookIdentity(item.book).displayName} <b>{oddsLabel(item.samples.at(-1).odds)}</b></span>)}</div>
      {movementSeries === 0 && <p>Every represented book currently has one stored sample. The chart shows evidence depth without implying movement that has not been captured.</p>}
    </>}
  </section>
}
